import type { Seat } from "@/lib/desk/career";

/**
 * Money Foundations — the real-life money habit each Desk promotion requires.
 * Savings first, basics learned, then steady paydown. Debt does not need to
 * reach $0 to advance: consistent paydown counts.
 * All targets are placeholders pending the owner's numbers.
 */

import { practiceNow } from "@/lib/practice/store";

export const MONEY_TRACK = "money";

export interface MoneyLesson {
  id: string;
  title: string;
  body: string[];
  question: string;
  options: string[];
  answer: number;
}

export const MONEY_LESSONS: MoneyLesson[] = [
  {
    id: "budget-basics",
    title: "Your first budget",
    body: [
      "A budget is a plan for your money before the month starts. Income minus spending should equal zero — every dollar gets a job.",
      "Start with what comes in after taxes, then list the bills that must be paid, then decide what goes to savings and debt, then what's left for everything else.",
    ],
    question: "In a zero-based budget, what should income minus planned spending equal?",
    options: ["Zero — every dollar has a job", "Whatever is left over", "10% of income"],
    answer: 0,
  },
  {
    id: "needs-wants",
    title: "Needs vs wants",
    body: [
      "Needs keep you safe and working: housing, food, utilities, transportation, minimum debt payments. Wants make life nicer but can wait.",
      "Most money leaks come from wants that turned into habits — subscriptions, eating out, small daily buys.",
    ],
    question: "Which is a need?",
    options: ["A streaming subscription", "The minimum payment on a loan", "A new phone upgrade"],
    answer: 1,
  },
  {
    id: "how-savings-works",
    title: "How savings works",
    body: [
      "Keep savings in a separate account so it's out of sight. A high-yield savings account pays interest while staying safe and easy to reach.",
      "Pay yourself first: move money to savings the day you get paid, automatically. Waiting to save what's left rarely works.",
    ],
    question: "What is the most reliable way to save?",
    options: ["Save whatever is left at month end", "An automatic transfer on payday", "Keep it in checking"],
    answer: 1,
  },
  {
    id: "fico-factors",
    title: "The 5 parts of a FICO score",
    body: [
      "Payment history (35%) — paying on time. Amounts owed (30%) — how much of your card limits you use. Length of history (15%). New credit (10%). Credit mix (10%).",
      "The two biggest levers are ones you control today: never miss a payment, and keep card balances low.",
    ],
    question: "Which factor weighs the most in a FICO score?",
    options: ["Credit mix", "Payment history", "New credit"],
    answer: 1,
  },
  {
    id: "utilization",
    title: "Credit utilization",
    body: [
      "Utilization is your card balances divided by your card limits. Owing $900 on a $3,000 limit is 30%.",
      "Under 30% is good, under 10% is best. Paying cards down is one of the fastest ways to lift a score.",
    ],
    question: "You owe $500 on a $5,000 limit. What is your utilization?",
    options: ["5%", "10%", "50%"],
    answer: 1,
  },
  {
    id: "credit-habits",
    title: "Habits that build credit",
    body: [
      "Set every bill to autopay at least the minimum so a payment is never late. One late payment can stay on a report for years.",
      "Don't close your oldest card — it keeps your history long. Apply for new credit only when you need it.",
    ],
    question: "What protects your payment history best?",
    options: ["Autopay on every bill", "Closing old cards", "Opening many new cards"],
    answer: 0,
  },
  {
    id: "snowball-avalanche",
    title: "Snowball vs avalanche",
    body: [
      "Snowball: pay minimums on everything, put extra on the smallest balance first. Quick wins keep you going.",
      "Avalanche: put extra on the highest interest rate first. Saves the most money. Either works — the one you'll stick with is the right one.",
    ],
    question: "The snowball method pays extra on which debt first?",
    options: ["Highest interest rate", "Smallest balance", "Newest debt"],
    answer: 1,
  },
  {
    id: "dti",
    title: "Debt-to-income (DTI)",
    body: [
      "DTI is your monthly debt payments divided by your monthly income before taxes. $1,200 in payments on $4,000 income is 30%.",
      "Lenders like 36% or less. Lowering DTI means more of your paycheck is yours to save and give.",
    ],
    question: "Payments of $800 on $4,000 monthly income is what DTI?",
    options: ["8%", "20%", "40%"],
    answer: 1,
  },
];

export interface MoneyCheckin {
  week_start: string;
  savings: number;
  total_debt: number;
  debt_paid: number;
  fico: number | null;
  monthly_income: number | null;
  monthly_debt_payments: number | null;
  monthly_expenses: number | null;
  /** Mortgage or rent; savings target = 12× this. */
  monthly_housing?: number | null;
  /** True Fast giving this week. */
  gave?: number | null;
  created_at?: string;
}

export interface MoneyHabits {
  savings_account_opened: boolean;
  auto_transfer_set: boolean;
  auto_transfer_amount: number | null;
  when_then_plan: string | null;
  spending_tracked?: boolean;
  bills_listed?: boolean;
}

export interface MoneyStats {
  setupDone: boolean;
  savingsOpened: boolean;
  autoTransfer: boolean;
  whenThen: boolean;
  spendingTracked: boolean;
  billsListed: boolean;
  recentGiving: number;
  lessonsDone: Set<string>;
  checkinWeeks: number;
  streak: number;
  missedTwo: boolean;
  checkedInThisWeek: boolean;
  savings: number;
  monthsSaved: number;
  firstDebt: number | null;
  latestDebt: number | null;
  debtDownPct: number;
  paydownMonths: number;
  firstFico: number | null;
  latestFico: number | null;
  dti: number | null;
  latest: MoneyCheckin | null;
  previous: MoneyCheckin | null;
  targets: MoneyTargets;
}

/** Personal targets built from the member's own baseline. */
export interface MoneyTargets {
  starterFund: number;
  starterWhy: string;
  stabilityDebtPct: number;
  freedomDebtPct: number;
  debtWhy: string;
  freedomDti: number;
  dtiWhy: string;
  tight: boolean;
}

export const DEFAULT_TARGETS: MoneyTargets = {
  starterFund: 1000, starterWhy: "Standard starter fund.",
  stabilityDebtPct: 20, freedomDebtPct: 50, debtWhy: "Standard paydown target.",
  freedomDti: 36, dtiWhy: "Lenders like 36% or less.", tight: false,
};

/**
 * Scale targets to what is realistic for this household.
 * Debt goals assume ~24 months at a safe payment (15% of take-home, 8% when the
 * budget is tight). Uses the latest income/expenses so a real change re-tailors.
 */
export function tailorTargets(income: number, expenses: number, debt: number | null, override?: Partial<MoneyTargets> | null): MoneyTargets {
  if (!(income > 0)) return { ...DEFAULT_TARGETS, ...(override ?? {}) };
  const margin = income - expenses;
  const tight = expenses > 0 && margin < income * 0.1;
  const starterFund = Math.min(2000, Math.max(500, Math.round((income * 0.4) / 100) * 100));
  const payment = income * (tight ? 0.08 : 0.15);
  const reachable24 = debt && debt > 0 ? Math.min(100, (payment * 24 / debt) * 100) : 100;
  const freedomDebtPct = Math.max(15, Math.min(50, Math.round(reachable24 / 5) * 5));
  const stabilityDebtPct = Math.max(10, Math.min(20, Math.round(freedomDebtPct / 2 / 5) * 5));
  const freedomDti = tight ? 43 : 36;
  const t: MoneyTargets = {
    starterFund,
    starterWhy: `About 10 days of your income (${usd(income)}/mo).`,
    stabilityDebtPct, freedomDebtPct,
    debtWhy: debt && debt > 0
      ? `Paying about ${usd(payment)}/mo for 2 years clears ~${Math.round(reachable24)}% of ${usd(debt)}.`
      : "No debt logged.",
    freedomDti,
    dtiWhy: tight ? "Your budget is tight, so the first goal is 43%." : "Lenders like 36% or less.",
    tight,
  };
  return { ...t, ...(override ?? {}) };
}

/** Monday of the week containing `d`, as YYYY-MM-DD (local). */
export function weekStart(d = practiceNow()): string {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}

const WEEK = 7 * 864e5;
const weekNum = (iso: string) => Math.round(new Date(iso + "T00:00:00").getTime() / WEEK);

function lastNonNull<K extends keyof MoneyCheckin>(rows: MoneyCheckin[], k: K) {
  for (let i = rows.length - 1; i >= 0; i--) if (rows[i][k] != null) return rows[i][k];
  return null;
}

/** Latest Metrics account balances (named accounts) so Foundation counts them without a separate check-in. */
export interface LiveBalances { savings?: { value: number; at: string } | null; debt?: { value: number; at: string } | null; hasSavingsAccount?: boolean }

/** Emergency-fund style cash: checking + savings accounts (investments/retirement excluded). */
export function liveFromMetrics(savingsDetails: { accounts?: { type?: string; balance?: number; cash?: number; money_market?: number; mode?: string }[] } | null, savingsAt: string | null, debtValue: number | null, debtAt: string | null): LiveBalances {
  const accts = savingsDetails?.accounts ?? [];
  const cash = accts.filter((a) => a.type === "checking" || a.type === "savings").reduce((s, a) => s + (Number(a.balance) || 0), 0);
  return {
    savings: savingsAt && accts.length ? { value: cash, at: savingsAt } : null,
    debt: debtAt && debtValue != null ? { value: Math.max(0, debtValue), at: debtAt } : null,
    hasSavingsAccount: accts.some((a) => a.type === "savings"),
  };
}

export function computeMoneyStats(
  checkinsIn: MoneyCheckin[],
  habits: MoneyHabits | null,
  lessons: string[],
  override?: Partial<MoneyTargets> | null,
  live?: LiveBalances | null,
): MoneyStats {
  const rows = [...checkinsIn].sort((a, b) => a.week_start.localeCompare(b.week_start));
  const weeks = [...new Set(rows.map((r) => weekNum(r.week_start)))].sort((a, b) => a - b);
  const nowW = weekNum(weekStart());
  const checkedInThisWeek = weeks.includes(nowW);

  // "Never miss twice": one skipped week keeps the streak; two in a row ends it.
  let streak = 0;
  let missedTwo = false;
  if (weeks.length) {
    const last = weeks[weeks.length - 1];
    if (nowW - last >= 3) missedTwo = true; // last two full weeks missed
    else {
      streak = 1;
      for (let i = weeks.length - 1; i > 0; i--) {
        if (weeks[i] - weeks[i - 1] <= 2) streak++;
        else break;
      }
    }
  }

  const latest = rows[rows.length - 1] ?? null;
  const previous = rows[rows.length - 2] ?? null;
  const liveSav = live?.savings && (!latest || live.savings.at.slice(0, 10) >= latest.week_start) ? live.savings.value : null;
  const savings = liveSav ?? (latest ? Number(latest.savings) : 0);
  const expenses = Number(lastNonNull(rows, "monthly_expenses") ?? 0);
  const firstDebt = rows.length ? Number(rows[0].total_debt) : null;
  const liveDebt = live?.debt && (!latest || live.debt.at.slice(0, 10) >= latest.week_start) ? live.debt.value : null;
  const latestDebt = liveDebt ?? (latest ? Number(latest.total_debt) : null);
  const debtDownPct =
    firstDebt && firstDebt > 0 && latestDebt != null ? Math.max(0, ((firstDebt - latestDebt) / firstDebt) * 100) : latestDebt === 0 && rows.length ? 100 : 0;

  // Consecutive months (ending at the latest) where month-end debt dropped.
  const monthEnd = new Map<string, number>();
  rows.forEach((r) => monthEnd.set(r.week_start.slice(0, 7), Number(r.total_debt)));
  const months = [...monthEnd.entries()];
  let paydownMonths = 0;
  for (let i = months.length - 1; i > 0; i--) {
    if (months[i][1] < months[i - 1][1] || (months[i][1] === 0 && months[i - 1][1] === 0)) paydownMonths++;
    else break;
  }

  const ficoRows = rows.filter((r) => r.fico != null);
  const income = Number(lastNonNull(rows, "monthly_income") ?? 0);
  const payments = lastNonNull(rows, "monthly_debt_payments");
  const dti = income > 0 && payments != null ? (Number(payments) / income) * 100 : null;

  const savingsOpened = !!habits?.savings_account_opened || !!live?.hasSavingsAccount;
  const autoTransfer = !!habits?.auto_transfer_set;
  const whenThen = !!habits?.when_then_plan?.trim();

  return {
    setupDone: savingsOpened && autoTransfer && whenThen,
    savingsOpened,
    autoTransfer,
    whenThen,
    spendingTracked: !!habits?.spending_tracked,
    billsListed: !!habits?.bills_listed,
    recentGiving: rows.slice(-4).filter((r) => Number(r.gave ?? 0) > 0).length,
    lessonsDone: new Set(lessons),
    checkinWeeks: weeks.length,
    streak,
    missedTwo,
    checkedInThisWeek,
    savings,
    monthsSaved: expenses > 0 ? savings / expenses : 0,
    firstDebt,
    latestDebt,
    debtDownPct,
    paydownMonths,
    firstFico: ficoRows[0]?.fico ?? null,
    latestFico: ficoRows[ficoRows.length - 1]?.fico ?? null,
    dti,
    latest,
    previous,
    targets: tailorTargets(income, expenses, firstDebt, override),
  };
}

export interface FoundationReq {
  key: string;
  label: string;
  hint: string;
  target: number;
  current: number;
  met: boolean;
  /** Overrides "current / target" on the checklist. */
  display?: string;
  /** What the member should tap to make progress. */
  action: "setup" | "checkin" | "lessons";
  /** Sow (earn, give) · Steward (keep) · Wisdom (know the rules). */
  tag?: "sow" | "steward" | "wisdom";
}

/**
 * Foundation — Decorum's climb built on Maslow's hierarchy of needs.
 * Levels 1–3 build the foundation (Stages 1–3); reaching Stability opens the Simulation.
 * Levels 4–5 run alongside the Desk ranks up to Mighty Man / Radiant Woman.
 * Habits stack: every level's `keep` habits must still hold for anything above to count.
 * All numbers are placeholders pending the owner's figures.
 */
export interface PyramidLevel {
  n: number;
  name: string;
  maslow: string;
  summary: string;
  why: string;
  lessons: string[];
}

export const PYRAMID: PyramidLevel[] = [
  { n: 1, name: "Survival", maslow: "Basic needs", summary: "See your money and start saving real dollars.", why: "You can't steward what you can't see. Light on the numbers comes first.", lessons: ["budget-basics", "needs-wants", "how-savings-works"] },
  { n: 2, name: "Safety", maslow: "Security", summary: "$1,000 buffer and how credit works.", why: "A small emergency fund turns crises into inconveniences and quiets fear.", lessons: ["fico-factors", "utilization", "credit-habits"] },
  { n: 3, name: "Stability", maslow: "Belonging and order", summary: "One month saved, steady paydown, DTI known.", why: "Order in the house frees the mind to learn. The Simulation opens here.", lessons: ["snowball-avalanche", "dti"] },
  { n: 4, name: "Freedom", maslow: "Esteem", summary: "Three months saved, debt halved, DTI under 36%.", why: "Margin creates choices. You act from strength, not pressure.", lessons: [] },
  { n: 5, name: "Legacy", maslow: "Self-actualization", summary: "Six months saved, consumer-debt free, True Fast giving as a habit.", why: "The Mighty Man and Radiant Woman give freely and keep doing it, because it's who they are.", lessons: [] },
];
export const SIM_PYRAMID_LEVEL = 3;

const usd = (v: number) => `$${Math.round(v).toLocaleString()}`;
const count = (have: number, need: number) => ({ current: Math.min(have, need), target: need, met: have >= need });
const bool = (ok: boolean) => ({ current: ok ? 1 : 0, target: 1, met: ok });

function lessonReq(lv: PyramidLevel, s: MoneyStats): FoundationReq | null {
  if (!lv.lessons.length) return null;
  const have = lv.lessons.filter((l) => s.lessonsDone.has(l)).length;
  return { key: `f_l${lv.n}_lessons`, label: `Finish ${lv.lessons.length} money lessons`, hint: "Short reads with one check question each.", action: "lessons", tag: "wisdom", ...count(have, lv.lessons.length) };
}

/** `build` = reached once; `keep` = habits that must keep holding. */
export function levelRequirements(n: number, s: MoneyStats): { build: FoundationReq[]; keep: FoundationReq[] } {
  const lv = PYRAMID[n - 1];
  const rhythm = s.checkinWeeks > 0 && !s.missedTwo;
  const T = s.targets ?? DEFAULT_TARGETS;
  // Lessons are taught in Simulation research, not the money checklist.
  void lessonReq; void lv;
  const L = null;
  const b: (FoundationReq | null)[] = [];
  const k: FoundationReq[] = [];
  const saved = (amt: number, key: string) => ({ key, label: `Save your first ${usd(amt)}`, hint: "Real dollars set aside. Log your savings in a check-in.", action: "checkin" as const, tag: "steward" as const, current: Math.min(s.savings, amt), target: amt, met: s.savings >= amt, display: `${usd(Math.min(s.savings, amt))} / ${usd(amt)}` });
  if (n === 1) {
    b.push(
      { key: "f_l1_track", label: "Track every dollar for 1 week", hint: "Write down every purchase for 7 days, then tick it in Setup.", action: "setup", tag: "steward", ...bool(s.spendingTracked) },
      { key: "f_l1_bills", label: "List every income and bill", hint: "Paycheck, rent, minimums, subscriptions. Tick it in Setup.", action: "setup", tag: "steward", ...bool(s.billsListed) },
      { key: "f_setup", label: "Start setting money aside each payday", hint: "A savings account or an envelope works. Automatic is a good idea, but it's your choice.", action: "setup", tag: "steward", ...bool(s.setupDone) },
      saved(100, "f_l1_save100"),
      saved(250, "f_l1_save250"),
      saved(500, "f_l1_save500"),
      { key: "f_l1_checkins", label: "4 weekly money check-ins", hint: "Sunday, about 3 minutes: savings, debt, paid this week.", action: "checkin", tag: "steward", ...count(s.checkinWeeks, 4) },
    );
    k.push(
      { key: "f_k1_auto", label: "Still setting money aside each payday", hint: "Keep paying yourself first.", action: "setup", tag: "steward", ...bool(s.autoTransfer) },
      { key: "f_k1_rhythm", label: "Weekly check-in rhythm (never miss twice)", hint: "One missed week is fine. Two in a row breaks the habit.", action: "checkin", tag: "steward", ...bool(rhythm) },
    );
  } else if (n === 2) {
    b.push(
      { key: "f_buffer", label: `${usd(T.starterFund)} starter emergency fund`, hint: `Why this number: ${T.starterWhy}`, action: "checkin", tag: "steward", current: Math.min(s.savings, T.starterFund), target: T.starterFund, met: s.savings >= T.starterFund, display: `${usd(s.savings)} / ${usd(T.starterFund)}` },
      L,
      { key: "f_fico", label: "Log your FICO score", hint: "Free from most banking apps. Log it once a month.", action: "checkin", tag: "wisdom", ...bool(s.latestFico != null) },
      { key: "f_l2_checkins", label: "8 weekly money check-ins", hint: "Keep the Sunday rhythm going.", action: "checkin", tag: "steward", ...count(s.checkinWeeks, 8) },
    );
    k.push({ key: "f_k2_buffer", label: `Savings stays at ${usd(T.starterFund)} or more`, hint: "If you dip into it, rebuild it first.", action: "checkin", tag: "steward", ...bool(s.savings >= T.starterFund), display: usd(s.savings) });
  } else if (n === 3) {
    const payOk = s.debtDownPct >= T.stabilityDebtPct || s.paydownMonths >= 3 || s.latestDebt === 0;
    b.push(
      { key: "f_month", label: "1 month of expenses saved", hint: "Add monthly expenses in your check-in.", action: "checkin", tag: "steward", current: Math.min(s.monthsSaved, 1), target: 1, met: s.monthsSaved >= 1, display: `${s.monthsSaved.toFixed(1)} / 1 mo` },
      { key: "f_paydown", label: `Debt down ${T.stabilityDebtPct}% or 3 months of paydown`, hint: `You don't need $0. Why this number: ${T.debtWhy}`, action: "checkin", tag: "steward", ...bool(payOk), display: `${Math.round(s.debtDownPct)}% · ${s.paydownMonths} mo` },
      { key: "f_dti", label: "Log your DTI", hint: "Add monthly income and debt payments in a check-in.", action: "checkin", tag: "wisdom", ...bool(s.dti != null) },
      L,
    );
    k.push(
      { key: "f_k3_month", label: "At least 1 month of expenses saved", hint: "Keep the cushion.", action: "checkin", tag: "steward", ...bool(s.monthsSaved >= 1) },
      { key: "f_k3_debt", label: "Debt not climbing back up", hint: "Debt stays at or below where you started.", action: "checkin", tag: "steward", ...bool(s.firstDebt == null || (s.latestDebt ?? 0) <= s.firstDebt) },
    );
  } else if (n === 4) {
    const debtOk = s.debtDownPct >= T.freedomDebtPct || s.latestDebt === 0;
    const ficoOk = (s.latestFico ?? 0) >= 700 || (s.firstFico != null && s.latestFico != null && s.latestFico - s.firstFico >= 50);
    b.push(
      { key: "f_three", label: "3 months of expenses saved", hint: "Your full emergency fund.", action: "checkin", tag: "steward", current: Math.min(s.monthsSaved, 3), target: 3, met: s.monthsSaved >= 3, display: `${s.monthsSaved.toFixed(1)} / 3 mo` },
      { key: "f_debt50", label: `Debt down ${T.freedomDebtPct}% (or $0)`, hint: `Measured from your first check-in. Why this number: ${T.debtWhy}`, action: "checkin", tag: "steward", ...bool(debtOk), display: `${Math.round(s.debtDownPct)}%` },
      { key: "f_fico700", label: "FICO 700+ or up 50 points", hint: "Measured from your first logged score.", action: "checkin", tag: "wisdom", ...bool(ficoOk), display: s.latestFico == null ? "not logged" : `${s.latestFico}` },
      { key: "f_streak", label: "12-week check-in streak", hint: "Missing one week is fine. Never miss twice.", action: "checkin", tag: "steward", ...count(s.streak, 12) },
    );
    k.push({ key: "f_k4_dti", label: `DTI ${T.freedomDti}% or less`, hint: `Monthly debt payments ÷ income. Why this number: ${T.dtiWhy}`, action: "checkin", tag: "wisdom", ...bool(s.dti != null && s.dti <= T.freedomDti), display: s.dti == null ? "not logged" : `${Math.round(s.dti)}%` });
  } else if (n === 5) {
    b.push(
      { key: "f_six", label: "6 months of expenses saved", hint: "Full margin for your household.", action: "checkin", tag: "steward", current: Math.min(s.monthsSaved, 6), target: 6, met: s.monthsSaved >= 6, display: `${s.monthsSaved.toFixed(1)} / 6 mo` },
      { key: "f_debtfree", label: "Consumer-debt free", hint: "Everything except the home is paid off.", action: "checkin", tag: "steward", ...bool(s.latestDebt === 0) },
    );
    k.push({ key: "f_k5_give", label: "True Fast giving in 3 of your last 4 check-ins", hint: "Log what you gave each week. Giving becomes a habit.", action: "checkin", tag: "sow", ...count(s.recentGiving, 3) });
  }
  return { build: b.filter(Boolean) as FoundationReq[], keep: k };
}

export interface PyramidStatus {
  /** Highest level fully built with every habit at or below it still kept. 0 = none yet. */
  level: number;
  /** Lowest level that was built but a habit slipped. */
  repair: number | null;
  /** The level being worked on now. */
  working: number;
  /** What to do now: repairs first, then the working level. */
  reqs: FoundationReq[];
  perLevel: { n: number; built: boolean; kept: boolean }[];
}

export function pyramidStatus(s: MoneyStats): PyramidStatus {
  const perLevel = PYRAMID.map((lv) => {
    const r = levelRequirements(lv.n, s);
    return { n: lv.n, built: r.build.every((x) => x.met), kept: r.keep.every((x) => x.met), r };
  });
  let level = 0;
  let repair: number | null = null;
  for (const p of perLevel) {
    if (!p.built) break;
    if (!p.kept) { repair = p.n; break; }
    level = p.n;
  }
  const working = Math.min(5, level + 1);
  let reqs: FoundationReq[];
  if (repair) {
    reqs = perLevel[repair - 1].r.keep.filter((x) => !x.met).map((x) => ({ ...x, label: `Repair: ${x.label}` }));
  } else if (level >= 5) {
    reqs = perLevel[4].r.keep;
  } else {
    const w = perLevel[working - 1].r;
    reqs = [...w.build, ...w.keep];
  }
  return { level, repair, working, reqs, perLevel: perLevel.map(({ n, built, kept }) => ({ n, built, kept })) };
}

/** Rank gate: Stability reached and every lower habit still kept. Same for every seat. */
export function foundationRequirements(_seat: Seat, s: MoneyStats): FoundationReq[] {
  const p = pyramidStatus(s);
  const ok = p.level >= SIM_PYRAMID_LEVEL && !s.missedTwo;
  return [{
    key: "f_pyramid", label: "Foundation: Stability kept", tag: "steward",
    hint: p.repair ? `Repair your ${PYRAMID[p.repair - 1].name} habits first.` : "Keep your weekly money habits going.",
    action: "checkin", ...bool(ok), display: `Level ${p.level} · ${p.level ? PYRAMID[p.level - 1].name : "not started"}`,
  }];
}

/** The single next action to show on the Hub. */
export function nextMoneyAction(reqs: FoundationReq[], s: MoneyStats): FoundationReq | null {
  const repair = reqs.find((r) => !r.met && r.label.startsWith("Repair"));
  if (repair) return repair;
  if (!s.checkedInThisWeek && s.setupDone) {
    const c = reqs.find((r) => r.action === "checkin");
    if (c) return { ...c, label: "Do this week's money check-in" };
  }
  return reqs.find((r) => !r.met) ?? null;
}
